/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import type {
  GodavariApplicationMetadata,
  GodavariContentId,
  GodavariMetadataStrings,
  GodavariNumericFields,
} from './GodavariMetadata';

export interface GodavariEventTime {
  event_wall_clock: string;
  event_time_zone: string;
}

export const GODAVARI_PULSE_STEPS = [
  'VideoUrlFetch',
  'LAUrl',
  'LicenceUrlFetch',
  'ManifestFetch',
  'ChunkDownload',
  'ConcurrencyCheck',
  'AdPlay',
] as const;
export const GODAVARI_PULSE_MICRO_STEPS = [
  'RequestStart',
  'ResponseEnd',
  'ParsingStart',
  'ParsingEnd',
  'ResponseError',
  'ChunkNumber',
  'NA',
] as const;
export const GODAVARI_PULSE_PLAYER_STATES = [
  'Buffering',
  'Ready',
  'Playing',
  'VideoUrl',
  'LAUrl',
  'LicenceUrl',
  'Manifest',
  'Chunk',
  'Concurrency',
  'Ad',
] as const;
export type GodavariPulseStep = (typeof GODAVARI_PULSE_STEPS)[number];
export type GodavariPulseMicroStep = (typeof GODAVARI_PULSE_MICRO_STEPS)[number];
export type GodavariPulsePlayerState = (typeof GODAVARI_PULSE_PLAYER_STATES)[number];
export type GodavariEndReason = 'UserIntent' | 'AutoRetry' | 'Binge' | 'AssetClick';

type RequestMicroStep = 'RequestStart' | 'ResponseEnd' | 'ResponseError' | 'NA';
export type GodavariStartupStage =
  | [
      'VideoUrlFetch' | 'LAUrl' | 'LicenceUrlFetch' | 'ManifestFetch',
      RequestMicroStep | 'ParsingStart' | 'ParsingEnd',
      GodavariPulsePlayerState,
    ]
  | ['ChunkDownload', RequestMicroStep | 'ChunkNumber', GodavariPulsePlayerState]
  | ['ConcurrencyCheck' | 'AdPlay', RequestMicroStep, GodavariPulsePlayerState];

export type GodavariPulseStage<T extends GodavariStartupStage = GodavariStartupStage> = T extends GodavariStartupStage
  ? {
      pulse_start_steps: T[0];
      pulse_micro_steps: T[1];
      pulse_player_state: T[2];
      pulse_player_retry?: 0 | 1;
    }
  : never;

export type GodavariPulseDetail = GodavariEventTime &
  GodavariPulseStage & {
    event_event_type: 'pulse_event_name';
    pulse_player_retry: 0 | 1;
  };

/** Application observations can start before player attachment, after session.start(). */
export type GodavariPulseObservation<T extends GodavariStartupStage = GodavariStartupStage> =
  T extends GodavariStartupStage
    ? Omit<GodavariPulseStage<T>, 'pulse_micro_steps'> & {
        pulse_micro_steps?: T[1];
        timestamp?: number;
        requestId?: string;
        retryFromStart?: boolean;
        autoPlayAllowed?: boolean;
      }
    : never;

export interface GodavariHeartbeatFields extends GodavariEventTime {
  event_current_playback_rate: number;
  event_start_position: number;
  event_end_position: number;
  event_duration: number;
  current_bitrate: number;
  event_estimated_bandwidth: number;
  event_available_buffer_duration: number;
  event_from_bitrate: number;
  event_to_bitrate: number;
  event_increment_number: number;
}

export interface GodavariVideoDetailData {
  heart_beat_event_buffer_health: { event_buffer_health: number[] };
  heart_beat_event_network_activity: {
    event_network_activity: { event_net_download_speed: number; event_content_type: string }[];
  };
  heart_beat_event_pause: object;
  heart_beat_event_resume: object;
  heart_beat_event_buffer_start: object;
  heart_beat_event_buffer_end: object;
  heart_beat_event_seek: object;
  heart_beat_event_upshift: { event_from_bitrate: number; event_to_bitrate: number };
  heart_beat_event_downshift: { event_from_bitrate: number; event_to_bitrate: number };
  heart_beat_event_dropped_frames: { event_increment_number: number };
  heart_beat_event_audio_language: { from_audio_language: string; to_audio_language: string };
  heart_beat_event_subtitle_language: { from_subtitle_language: string; to_subtitle_language: string };
}

export type GodavariVideoDetailInput = {
  [K in keyof GodavariVideoDetailData]: { event_event_type: K } & GodavariVideoDetailData[K];
}[keyof GodavariVideoDetailData];
export type GodavariVideoDetail = GodavariHeartbeatFields & GodavariVideoDetailInput;

export type GodavariAdDetailType =
  | 'heart_beat_ads_event_ad_click'
  | 'heart_beat_ads_event_ad_skip'
  | 'heart_beat_ad_skip_true_view'
  | 'heart_beat_ads_event_ad_pause'
  | 'heart_beat_ads_event_ad_resume'
  | 'heart_beat_ads_event_ad_buffer_start'
  | 'heart_beat_ads_event_ad_buffer_end';

export type GodavariAdDetail = GodavariHeartbeatFields & { event_event_type: GodavariAdDetailType };

export interface GodavariPlaybackFields extends GodavariEventTime, GodavariMetadataStrings, GodavariNumericFields {
  video_content_id: GodavariContentId;
  custom_tags: Record<string, unknown>;
}

export interface GodavariDeviceInformation {
  model_name?: string;
  device_name?: string;
  device_model?: string;
  device_operating_system?: string;
  device_operating_system_version?: string;
  browser_name?: string;
  browser_version?: string;
  manufacturer_name?: string;
}

export interface GodavariFrontendConfiguration {
  BEACON_BASE_URL: string;
  GODAVARI_AD_HEARTBEAT_INTERVAL: string;
  GODAVARI_ENABLED: boolean;
  GODAVARI_ERROR_RETRYINTERVAL: number;
  GODAVARI_HEARTBEAT_INTERVAL: string;
}

export type GodavariApplicationPayload = Omit<GodavariApplicationMetadata, 'app_user_age'> &
  GodavariEventTime & {
    app_user_age: number;
    custom_tags?: Record<string, unknown>;
  };

export const GODAVARI_LIFECYCLE_HEADERS = {
  videoSessionStart: { video_event_type: 'videoSessionStart', event_event_type: 'videoSessionStart' },
  videoAttempt: { video_event_type: 'VideoAttempt', event_event_type: 'Attempt' },
  videoPlay: { video_event_type: 'VideoPlay', event_event_type: 'Play' },
  videoError: { video_event_type: 'VideoError', event_event_type: 'VideoError' },
  videoSessionAppBackGround: { video_event_type: 'AppBackgrounded', event_event_type: 'AppBackgrounded' },
  videoSessionAppForeGround: { video_event_type: 'AppForegrounded', event_event_type: 'AppForegrounded' },
  videoEnd: { video_event_type: 'VideoEnd', event_event_type: 'VideoEnd' },
  videoSessionEnd: { video_event_type: 'VideoSessionEnd', event_event_type: 'VideoSessionEnd' },
  adsVmapRequestStart: { video_event_type: 'vmaprequeststart', event_event_type: 'vmaprequeststart' },
  adsVmapRequestEnd: { video_event_type: 'vmaprequestend', event_event_type: 'vmaprequestend' },
  adsAttempt: { video_event_type: 'AdAttempt', event_event_type: 'AdAttempt' },
  adsPlay: { video_event_type: 'AdPlay', event_event_type: 'AdPlay' },
  adsPlayImpression: { video_event_type: 'AdImpressionDelayed', event_event_type: 'AdImpressionDelayed' },
  adsError: { video_event_type: 'AdError', event_event_type: 'AdError' },
  adsEnd: { video_event_type: 'AdEnd', event_event_type: 'AdEnd' },
} as const;

export type GodavariLifecyclePayload<K extends keyof typeof GODAVARI_LIFECYCLE_HEADERS> = GodavariPlaybackFields &
  (typeof GODAVARI_LIFECYCLE_HEADERS)[K] & { event_data_list?: never };

export type GodavariLifecyclePayloads = {
  [K in keyof typeof GODAVARI_LIFECYCLE_HEADERS]: GodavariLifecyclePayload<K>;
};

export interface GodavariBatchPayloads {
  videoSessionPulse: GodavariPlaybackFields & {
    video_event_type: 'Pulse';
    event_event_type: 'Pulse';
    pulse_event_count_sequence: number;
    event_data_list: GodavariPulseDetail[];
  };
  videoSessionHeartbeat: GodavariPlaybackFields & {
    video_event_type: 'VideoHeartbeat';
    event_event_type: 'VideoHeartbeat';
    event_data_list: GodavariVideoDetail[];
  };
  adsHeartbeat: GodavariPlaybackFields & {
    video_event_type: 'AdHeartbeat';
    event_event_type: 'AdHeartbeat';
    event_data_list: GodavariAdDetail[];
  };
}

export type GodavariConnectorPayloads = GodavariLifecyclePayloads & GodavariBatchPayloads;
export type GodavariConnectorMethods = {
  [K in keyof GodavariConnectorPayloads]: (
    eventName: GodavariConnectorPayloads[K]['video_event_type'],
    payload: GodavariConnectorPayloads[K],
  ) => void;
};

export interface GodavariEmptyBatch {
  event_data_list: [];
}
